import * as PIXI from "pixi.js";

export interface AssetsConfig {
  baseUrl?: string;
  version?: string;
}

let initialized = false;
const VITE_CDN_URL = import.meta.env.VITE_CDN_URL || "";
const VITE_CDN_PATH = import.meta.env.VITE_CDN_PATH || "";

const DEFAULT_BASE_URL = `${VITE_CDN_URL}${VITE_CDN_PATH}`;

let config: AssetsConfig = {
  baseUrl: DEFAULT_BASE_URL,
  version: "",
};

// Cache duration: 1 hour in milliseconds
const CACHE_DURATION_MS = 1 * 60 * 60 * 1000;

/**
 * Cache buster key that changes every 24 hours.
 * All CDN URLs get this param so the browser treats them as new
 * once per day and re-fetches from the origin.
 */
function getCacheBuster(): string {
  const now = Date.now();
  const dayStart = Math.floor(now / CACHE_DURATION_MS) * CACHE_DURATION_MS;
  return dayStart.toString(36);
}

export function configureAssets(newCfg: AssetsConfig) {
  config = { ...config, ...newCfg };
}

export function getAssetsConfig() {
  return { ...config };
}

/**
 * Возвращает суффикс mip-уровня, основываясь на физической ширине экрана
 * (CSS-ширина × devicePixelRatio).
 *
 * AssetPack генерирует 3 варианта: 1x (без суффикса), @0.5x, @0.25x.
 * Лоадер (`/loading/`) генерируется только в 1x, поэтому суффикс к нему
 * никогда не применяется.
 */
export function getResolutionSuffix(): string {
  if (typeof window === "undefined") return "";
  const physicalWidth =
    (window.innerWidth || 0) * (window.devicePixelRatio || 1);
  if (physicalWidth >= 2048) return "";
  if (physicalWidth >= 1024) return "@0.5x";
  return "@0.25x";
}

/**
 * Возвращает числовой эквивалент текущего суффикса mip-уровня.
 * 1 — полный размер, 0.5 / 0.25 — уменьшенные mip-карты.
 */
export function getAssetResolution(): number {
  const suffix = getResolutionSuffix();
  if (suffix === "") return 1;
  if (suffix === "@0.5x") return 0.5;
  return 0.25;
}

export async function ensureAssetsInit() {
  if (initialized) return;
  await PIXI.Assets.init({
    texturePreference: {
      resolution: getAssetResolution(),
      format: ["webp", "png"],
    },
  });
  initialized = true;
}

export function buildAssetUrl(path: string) {
  const { baseUrl, version } = config;
  const isAbsolute = /^https?:\/\//i.test(path);
  let url = isAbsolute ? path : `${baseUrl || ""}${path}`;
  if (version) {
    url += `${url.includes("?") ? "&" : "?"}v=${encodeURIComponent(version)}`;
  }
  // Daily cache buster for CDN URLs — forces re-fetch every 24 h
  if (VITE_CDN_URL && url.includes(VITE_CDN_URL)) {
    url += `${url.includes("?") ? "&" : "?"}_cb=${getCacheBuster()}`;
  }
  return url;
}

/**
 * Подменяет URL ассета на нужный mip-уровень.
 * Используется для прямых загрузок (Loader.tsx, skinStore), где
 * мы передаём один путь, а не массив `src` из манифеста.
 *
 * Пропускает: уже масштабированные пути (`@<x>x.`) и файлы лоадера
 * (`/loading/`), который генерируется только в 1x.
 */
export function applyAssetResolution(path: string): string {
  const suffix = getResolutionSuffix();
  if (suffix === "") return path;
  if (/@\d+(?:\.\d+)?x(?=\.|$)/.test(path)) return path;
  if (/(^|[\\/])loading[\\/]/i.test(path)) return path;

  // Поддерживаем расширения вида .png, .webp, .png.atlas, .webp.atlas,
  // .png.json, .webp.json (для PIXI-spritesheet/Spine атласа).
  const m = path.match(/^(.*?)(\.(?:png|webp))(\.(?:atlas|json))?(\?.*)?$/i);
  if (!m) return path;
  const [, base, imgExt, subExt = "", query = ""] = m;
  return `${base}${suffix}${imgExt}${subExt}${query}`;
}
